<template>
    <div class="c-tr">
        <div class="c-td is-1">
            <span v-text="'Numer dokumentu'" class="typo-label" />
            <span class="d-flex align-items-center">
                {{ doc.fee_no }}
                <span
                    v-if="isUnpaidDocument"
                    v-text="'!'"
                    v-tooltip="unpaidDocumentTooltip"
                    class="a-counter ml-2"
                />
            </span>
        </div>
        <div class="c-td is-2">
            <span v-text="'Termin płatności'" class="typo-label" />
            {{ dueDate }}
        </div>
        <div class="c-td is-3">
            <span v-text="'Status dokumentu'" class="typo-label" />
            <span
                v-if="status"
                v-text="status.text"
                class="status w-100"
                :class="status.class"
            />
            <span v-else v-text="'---'" />
        </div>
        <div class="c-td is-4">
            <span v-text="'Kwota netto'" class="typo-label" />
            {{ formattedNet }}
        </div>
        <div class="c-td is-4">
            <span v-text="'Wartość VAT'" class="typo-label" />
            {{ formattedTax }}
        </div>
        <div class="c-td is-4">
            <span v-text="'Kwota brutto'" class="typo-label" />
            {{ formattedGross }}
        </div>
        <div class="c-td is-5">
            <span v-text="'Raport'" class="typo-label" />
            <the-btn
                v-tooltip="reportTooltip"
                class="btn-outline-custom btn-sm"
                text="Pobierz"
                icon="Download"
                :loading-status="isReportDownloading ? 'loading' : 'idle'"
                :disabled="!attachment('report')"
                @click="download('report')"
            />
        </div>
        <div class="c-td is-5">
            <span v-text="'Faktura VAT'" class="typo-label" />
            <the-btn
                v-tooltip="accountingTooltip"
                class="btn-block btn-sm"
                text="Pobierz"
                icon="Download"
                :loading-status="isAccountingDownloading ? 'loading' : 'idle'"
                :disabled="!attachment('accounting')"
                @click="download('accounting')"
            />
        </div>
        <div class="c-td is-6">
            <span v-if="isPayable" v-text="'Płatność'" class="typo-label" />
            <the-btn
                v-if="isPayable"
                class="btn-secondary btn-sm"
                text="Opłać dokument"
                :icon="isPaying ? 'Spinner' : ''"
                :loading-status="isPaying ? 'loading' : 'idle'"
                :disabled="isPaying"
                @click="goToPayment"
            />
        </div>
    </div>
</template>

<script>
import moment from 'moment'
import { mapActions } from 'vuex'
import { PAID_STATUSES, UNPAID_STATUSES, EXPIRED_STATUSES, PAYMENT_STATUSES, DOCUMENT_TYPES } from '@/utils/constants/drs/payment'

const TOOLTIPS = {
    unpaidYearly: date => `Jeśli nie opłacisz dokumentu do ${date} godz. 23.59 taryfa zmieni się na miesięczną`,
    unpaidMonthly: 'Brak płatności zrealizowanej w terminie skutkuje wstrzymaniem procesu przesyłania danych z urządzeń.<br/>W efekcie wykonanie m.in. takich czynności jak przyjęcie opakowań lub wydanie kuponów stanie się niemożliwe.',
    report: 'Raport dostępny wkrótce',
    zeroAccounting: 'Ten dokument nie wymaga faktury',
    ksefAccounting: 'Fakturę znajdziesz w Krajowym Systemie e-Faktur (KSeF)'
}

export default {
    props: {
        doc: { type: Object, required: true }
    },

    data: () => ({
        loadingFiles: {},
        isPaying: false,
        isReportDownloading: false,
        isAccountingDownloading: false
    }),

    computed: {
        dueDate() {
            return moment(this.doc.due_date).format('DD-MM-YYYY')
        },

        formattedNet() {
            return this.price(this.doc.net)
        },
        formattedTax() {
            return this.price(this.doc.tax)
        },
        formattedGross() {
            return this.price(this.doc.gross)
        },

        status() {
            const s = this.doc.status

            if (PAID_STATUSES.includes(s)) {
                return { class: 'is-success', text: 'Opłacony' }
            }

            if (UNPAID_STATUSES.includes(s)) {
                return { class: 'is-warning', text: 'Nieopłacony' }
            }

            if (EXPIRED_STATUSES.includes(s)) {
                return { class: 'is-error', text: 'Zaległy' }
            }

            if (s === PAYMENT_STATUSES.CLOSED) {
                return { class: 'is-gray', text: 'Zamknięty' }
            }

            return null
        },

        isPayable() {
            return this.isUnpaidDocument || EXPIRED_STATUSES.includes(this.doc.status)
        },

        isUnpaidDocument() {
            return UNPAID_STATUSES.includes(this.doc.status)
        },

        unpaidDocumentTooltip() {
            return this.doc.type === DOCUMENT_TYPES.YEARLY
                ? TOOLTIPS.unpaidYearly(this.dueDate)
                : TOOLTIPS.unpaidMonthly
        },

        reportTooltip() {
            return !this.attachment('report') && TOOLTIPS.report
        },

        accountingTooltip() {
            if (this.attachment('accounting')) {
                return ''
            }

            return this.doc.status === PAYMENT_STATUSES.ZERO
                ? TOOLTIPS.zeroAccounting
                : TOOLTIPS.ksefAccounting
        }
    },

    methods: {
        ...mapActions('payment', ['downloadDocumentFile', 'fetchPaymentLinkByDocId']),

        price(value) {
            return this.$options.filters.currency(value / 100)
        },

        attachment(type) {
            return this.doc.document_attachments.find(el => el.document_type === type)
        },

        async download(type) {
            const key = type === 'report' ? 'isReportDownloading' : 'isAccountingDownloading'
            this[key] = true

            const file = this.attachment(type)
            await this.downloadDocumentFile(file.path)

            this[key] = false
        },

        async goToPayment() {
            this.isPaying = true

            const paymentLink = await this.fetchPaymentLinkByDocId(this.doc.id)

            if (!paymentLink) {
                this.$store.dispatch('addNotification', {
                    type: 'danger',
                    content: 'Wystąpił błąd'
                })
            } else {
                window.location.href = paymentLink
            }

            this.isPaying = false
        }
    }
}
</script>

<style lang="scss">
.is-1 .a-counter {
    --size: 1.2rem;
    flex: 0 0 var(--size);
    font-size: 0.625rem;
}
</style>